import { useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { RolloutEventsTimeseriesBucket } from '@/lib/software-update-rollout.service';
import { typography as t, radius } from '@/theme/tokens';

export const ROLLOUT_EVENT_TYPES = [
  'download_success',
  'download_failed',
  'download_rejected',
  'install_success',
  'install_failed',
] as const;
export type RolloutTimeseriesEventType = (typeof ROLLOUT_EVENT_TYPES)[number];

export type ChartMode = 'totals' | 'reasons';

// Stable colors for the fixed event-type lines (shared by both chart modes).
// download_rejected is violet (#6D28D9), not the old orange — the orange was
// indistinguishable from download_failed (#EA580C) for full and impaired color
// vision alike.
const EVENT_LINE_COLOR: Record<string, string> = {
  download_success: '#1D4ED8',
  download_failed: '#EA580C',
  download_rejected: '#6D28D9',
  install_success: '#047857',
  install_failed: '#B42318',
};

// Palette for reason-split lines that don't map to a fixed event-type color.
const REASON_LINE_PALETTE = [
  '#DC2626',
  '#D97706',
  '#0891B2',
  '#DB2777',
  '#CA8A04',
  '#4338CA',
  '#9333EA',
  '#0F766E',
  '#B45309',
  '#BE123C',
] as const;

function lineColorForKey(key: string, index: number): string {
  return EVENT_LINE_COLOR[key] ?? REASON_LINE_PALETTE[index % REASON_LINE_PALETTE.length];
}

const ACTIVE_DOT = { r: 4, strokeWidth: 2, stroke: '#fff' } as const;

function renderSeriesLine(key: string, color: string) {
  return (
    <Line
      key={key}
      // Linear segments: gentle enough to read a trend, without the spline
      // overshoot that made small failure series look like decorative waves.
      type="linear"
      dataKey={key}
      name={key}
      stroke={color}
      strokeWidth={2}
      dot={false}
      activeDot={ACTIVE_DOT}
      isAnimationActive={false}
    />
  );
}

// Gray legend labels with a colored swatch (recharts colors the label with the
// series color by default, which turns the legend into a band of competing hues).
function renderLegendLabel(value: string) {
  return <span style={{ color: '#6B7280' }}>{value}</span>;
}

function seriesKey(eventType: string, reason: string | null): string {
  return reason ? `${eventType} / ${reason}` : eventType;
}

function formatBucketSize(bucketSize: string): string {
  const secondsMatch = /^(\d+)s$/.exec(bucketSize);
  if (!secondsMatch) return bucketSize;

  const seconds = Number(secondsMatch[1]);
  if (seconds % 86_400 === 0) return `${seconds / 86_400}d`;
  if (seconds % 3_600 === 0) return `${seconds / 3_600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return bucketSize;
}

interface ChartTooltipEntry {
  name?: string;
  value?: number;
  color?: string;
}

function ChartTooltip({
  active,
  label,
  payload,
}: {
  active?: boolean;
  label?: string;
  payload?: ChartTooltipEntry[];
}) {
  if (!active || !payload?.length) return null;
  // Show only series that have data at this point, biggest first.
  const rows = payload
    .filter((p) => typeof p.value === 'number' && p.value > 0)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  if (rows.length === 0) return null;
  const when = label ? new Date(label) : null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${radius.md}px`,
        boxShadow: '0 4px 16px rgba(17,17,17,0.10)',
        px: 1.25,
        py: 1,
        minWidth: 160,
      }}
    >
      {when && !Number.isNaN(when.getTime()) && (
        <Typography
          sx={{ fontSize: 11, fontWeight: t.fontWeight.medium, color: 'text.secondary', mb: 0.75 }}
        >
          {when.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {rows.map((row) => (
          <Box
            key={row.name}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 11.5 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: row.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', flex: 1, mr: 1.5 }}>
              {row.name}
            </Typography>
            <Typography
              sx={{
                fontSize: 11.5,
                fontWeight: t.fontWeight.semibold,
                fontVariantNumeric: 'tabular-nums',
                color: 'text.primary',
              }}
            >
              {(row.value ?? 0).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TwoLineDateTick(props: {
  x?: number;
  y?: number;
  payload?: { value: string };
}) {
  const { x = 0, y = 0, payload } = props;
  const raw = payload?.value;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return (
      <text x={x} y={y} dy={12} textAnchor="middle" fontSize={11} fill="#9ca3af">
        {raw}
      </text>
    );
  }
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fontSize={11} fill="#9ca3af" dy={12}>
        {time}
      </text>
      <text textAnchor="middle" fontSize={10} fill="#b5bcc4" dy={26}>
        {date}
      </text>
    </g>
  );
}

interface TimeWindow {
  since: string;
  until: string;
}

export interface EventsTimeseriesChartProps {
  buckets: RolloutEventsTimeseriesBucket[];
  bucketSize: string;
  loading: boolean;
  /** 'totals' draws the five fixed event-type lines; 'reasons' splits by reason. */
  chartMode?: ChartMode;
  /** One of ROLLOUT_EVENT_TYPES to isolate a single line; anything else shows all. */
  eventTypeFilter?: string;
  /** When provided, drag-select on the plot emits a zoom window. */
  onZoom?: (window: TimeWindow) => void;
  /** Message shown when there are no buckets. */
  emptyText?: string;
  /** Plot height in px. */
  height?: number;
}

/**
 * Shared events timeseries line chart used by the rollout-detail and
 * software-update overview pages so both keep one learned color vocabulary and
 * one tooltip/axis treatment. Drag-to-zoom is enabled only when `onZoom` is set.
 */
export function EventsTimeseriesChart({
  buckets,
  bucketSize,
  loading,
  chartMode = 'totals',
  eventTypeFilter,
  onZoom,
  emptyText = 'No events in this window.',
  height = 240,
}: EventsTimeseriesChartProps) {
  const isIsolated = ROLLOUT_EVENT_TYPES.includes(eventTypeFilter as RolloutTimeseriesEventType);
  const showLine = (key: RolloutTimeseriesEventType) => !isIsolated || eventTypeFilter === key;

  // In "reasons" mode the buckets carry a dynamic `series` array; flatten each
  // bucket into a row keyed by `${event_type} / ${reason}` so Recharts can draw
  // one line per series key. Success/reason-null events stay as plain
  // event-type keys.
  const { groupedData, groupedKeys } = useMemo(() => {
    if (chartMode !== 'reasons') return { groupedData: [], groupedKeys: [] as string[] };
    const keys = new Set<string>();
    const rows = buckets.map((b) => {
      const row: Record<string, string | number> = { time: b.time };
      for (const s of b.series ?? []) {
        const key = seriesKey(s.event_type, s.reason);
        keys.add(key);
        row[key] = ((row[key] as number) ?? 0) + s.count;
      }
      return row;
    });
    const sortedKeys = Array.from(keys).sort();
    // Backfill missing keys with 0 so lines render continuously across buckets.
    for (const row of rows) {
      for (const key of sortedKeys) {
        if (row[key] == null) row[key] = 0;
      }
    }
    return { groupedData: rows, groupedKeys: sortedKeys };
  }, [chartMode, buckets]);

  const [refLeft, setRefLeft] = useState<string | null>(null);
  const [refRight, setRefRight] = useState<string | null>(null);

  const isEmpty = !loading && buckets.length === 0;
  const chartData = chartMode === 'reasons' ? groupedData : buckets;
  const zoomEnabled = !!onZoom;

  const handleMouseDown = (e: { activeLabel?: string }) => {
    if (!zoomEnabled) return;
    if (e?.activeLabel) {
      setRefLeft(e.activeLabel);
      setRefRight(e.activeLabel);
    }
  };
  const handleMouseMove = (e: { activeLabel?: string }) => {
    if (!zoomEnabled) return;
    if (refLeft && e?.activeLabel) setRefRight(e.activeLabel);
  };
  const handleMouseUp = () => {
    if (!zoomEnabled) return;
    if (refLeft && refRight && refLeft !== refRight) {
      const [a, b] = [refLeft, refRight].sort();
      onZoom?.({ since: a, until: b });
    }
    setRefLeft(null);
    setRefRight(null);
  };

  return (
    <Box sx={{ px: 2, pb: 1.5, height, position: 'relative' }}>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <CircularProgress size={20} />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              cursor: zoomEnabled ? 'crosshair' : 'default',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <CartesianGrid vertical={false} stroke="#f1f3f5" strokeDasharray="0" />
                <XAxis
                  dataKey="time"
                  tick={<TwoLineDateTick />}
                  interval="preserveStartEnd"
                  minTickGap={50}
                  height={40}
                  axisLine={false}
                  tickLine={false}
                  tickMargin={6}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                  tickMargin={6}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={renderLegendLabel}
                  wrapperStyle={{ fontSize: 11.5, paddingTop: 8 }}
                />
                {chartMode === 'reasons'
                  ? groupedKeys.map((key, i) => renderSeriesLine(key, lineColorForKey(key, i)))
                  : ROLLOUT_EVENT_TYPES.filter(showLine).map((key) =>
                      renderSeriesLine(key, EVENT_LINE_COLOR[key]),
                    )}
                {zoomEnabled && refLeft && refRight && refLeft !== refRight && (
                  <ReferenceArea
                    x1={refLeft}
                    x2={refRight}
                    strokeOpacity={0.3}
                    fill="#1D4ED8"
                    fillOpacity={0.12}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          {bucketSize && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 12,
                fontSize: 10,
                color: 'text.disabled',
                bgcolor: 'rgba(255,255,255,0.85)',
                px: 0.75,
                borderRadius: '3px',
              }}
            >
              {formatBucketSize(bucketSize)} buckets
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

export default EventsTimeseriesChart;
